import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, Minus, Plus, Info, Shield, Zap, Building2, Gift, ChevronLeft, ChevronRight } from "lucide-react";
import pricingTiers from "../landing/pricing-tiers.json";
import { apiGet, apiPost } from "../../utils/api";
import { getRole, syncSessionProfile } from "../../utils/authService";
import { applyPlanDisplayCopy, getPlanDisplayName, getPlanPublicId, getStoredPlanId } from "../../utils/pricingDisplay.js";
import { toast } from "../../utils/toast";
import { PillSelect } from "../common/ui.jsx";
import { SUPPORT_EMAIL } from "../../utils/supportContacts";
import "./billing.css";
import "../devices/devices.css";

const RAZORPAY_CHECKOUT_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";
const SALES_EMAIL = SUPPORT_EMAIL;
const MAX_AGENT_COUNT_ALLOWED = 100000;
const HISTORY_PAGE_SIZE = 25;

// Priority for upgrade/downgrade direction detection (matches backend PLAN_PRIORITY)
const TIER_PRIORITY = { free: 1, trial: 1, essentials: 2, basic: 3, pro: 3, enterprise: 4 };

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function isValidEmailAddress(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function normalizePhoneInput(value) {
  return String(value || "").replace(/[^\d+]/g, "").slice(0, 20);
}

function parseAgentCount(value) {
  const parsed = Number.parseInt(String(value || "").trim(), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  if (parsed > MAX_AGENT_COUNT_ALLOWED) return null;
  return parsed;
}

function clampAgentCount(value, min, max) {
  const parsed = parseAgentCount(value);
  const safeMin = Number.isFinite(Number(min)) && Number(min) > 0 ? Number(min) : 1;
  const safeMax = Number.isFinite(Number(max)) && Number(max) >= safeMin ? Number(max) : safeMin;
  if (!parsed) return safeMin;
  return Math.min(Math.max(parsed, safeMin), safeMax);
}

function readActiveSeatCount(subscription) {
  const parsedSeatCount = Number.parseInt(subscription?.seatCount || subscription?.maxSystems, 10);
  return Number.isFinite(parsedSeatCount) && parsedSeatCount > 0 ? parsedSeatCount : null;
}

function formatPrice(priceInPaise, currency = "INR") {
  if (!Number.isFinite(priceInPaise) || priceInPaise <= 0) {
    return "Free";
  }

  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(priceInPaise / 100);
  } catch {
    return `${(priceInPaise / 100).toFixed(2)} ${currency}`;
  }
}

function formatCatalogPrice(priceInPaise, currency = "INR", {
  allowFree = false,
  unavailableLabel = "Pricing unavailable",
} = {}) {
  if (!Number.isFinite(priceInPaise) || priceInPaise < 0) {
    return unavailableLabel;
  }
  if (priceInPaise === 0) {
    return allowFree ? "Free" : unavailableLabel;
  }
  return formatPrice(priceInPaise, currency);
}

function formatExpiryDate(isoDate) {
  if (!isoDate) return "";
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function formatDateTime(isoDate) {
  if (!isoDate) return "";
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getDefaultPaidPlan(tiers = []) {
  return tiers.find((item) => getPlanPublicId(item.id) === "essentials")
    || tiers.find((item) => getPlanPublicId(item.id) === "pro")
    || tiers.find((item) => getPlanPublicId(item.id) === "enterprise")
    || null;
}

function normalizeEntitlements(rawValue) {
  if (!Array.isArray(rawValue)) return [];
  return Array.from(
    new Set(
      rawValue
        .map((entry) => String(entry || "").trim().toLowerCase())
        .filter(Boolean)
    )
  );
}

function normalizeTenantQuote(rawValue) {
  if (!rawValue || typeof rawValue !== "object") return null;
  const seatCount = Number.parseInt(rawValue.seatCount, 10);
  const unitPriceInPaise = Number(rawValue.unitPriceInPaise);
  const priceInPaise = Number(rawValue.priceInPaise);
  const quote = {
    id: String(rawValue.id || "").trim(),
    seatCount: Number.isFinite(seatCount) && seatCount > 0 ? seatCount : null,
    unitPriceInPaise: Number.isFinite(unitPriceInPaise) && unitPriceInPaise >= 0 ? unitPriceInPaise : 0,
    priceInPaise: Number.isFinite(priceInPaise) && priceInPaise >= 0 ? priceInPaise : 0,
    currency: String(rawValue.currency || "INR").trim().toUpperCase(),
    updatedAt: rawValue.updatedAt || null,
  };
  return quote.id || quote.seatCount || quote.priceInPaise > 0 ? quote : null;
}

function resolveEnterpriseInitialSeatCount(tier, currentSubscription) {
  if (!tier || tier.id !== "enterprise") return "";

  const activePlanId = getStoredPlanId(currentSubscription?.planId);
  if (activePlanId === "enterprise" && currentSubscription?.isActive) {
    const currentSeats = Number.parseInt(currentSubscription?.seatCount || currentSubscription?.maxSystems, 10);
    if (Number.isFinite(currentSeats) && currentSeats > 0) {
      return String(currentSeats);
    }
  }

  const quotedSeats = Number.parseInt(tier?.tenantQuote?.seatCount, 10);
  if (Number.isFinite(quotedSeats) && quotedSeats > 0) {
    return String(quotedSeats);
  }

  return "1";
}

function resolveCheckoutInitialAgentCount(tier, currentSubscription, source = "tier") {
  if (!tier) return "";
  if (getPlanPublicId(tier.id) === "free") {
    const freeTrialLimit = Number.parseInt(tier?.seatLimit || tier?.maxSystems, 10);
    return String(Number.isFinite(freeTrialLimit) && freeTrialLimit > 0 ? freeTrialLimit : 3);
  }
  if (tier.id === "enterprise") {
    return resolveEnterpriseInitialSeatCount(tier, currentSubscription);
  }

  if (source === "agents") return "1";

  const activePlanId = getStoredPlanId(currentSubscription?.planId);
  const activeSeatCount = readActiveSeatCount(currentSubscription);
  if (activePlanId === getStoredPlanId(tier.id) && currentSubscription?.isActive && activeSeatCount) {
    return String(activeSeatCount);
  }

  return "1";
}

function mergeTiersWithServer(serverPlans = []) {
  const byId = new Map(
    (Array.isArray(serverPlans) ? serverPlans : [])
      .filter((plan) => plan && typeof plan.id === "string")
      .map((plan) => [getStoredPlanId(plan.id), plan])
  );

  return pricingTiers.map((tier) => {
    const storedTierId = getStoredPlanId(tier.id);
    const hasServerPlan = byId.has(storedTierId);
    const serverPlan = byId.get(storedTierId) || {};
    const isContactSales = typeof serverPlan.contactSales === "boolean"
      ? serverPlan.contactSales
      : !!tier.contactSales;
    const fallbackTierPriceInPaise = Number(tier.priceInPaise || 0);
    const rawServerPriceInPaise = Number(serverPlan.priceInPaise);
    const priceInPaise = Number.isFinite(rawServerPriceInPaise)
      ? rawServerPriceInPaise
      : fallbackTierPriceInPaise;
    const requiresPayment = typeof serverPlan.requiresPayment === "boolean"
      ? serverPlan.requiresPayment
      : !!tier.requiresPayment;
    const hasPricingError = getPlanPublicId(tier.id) !== "free"
      && !isContactSales
      && hasServerPlan
      && (!requiresPayment || !Number.isFinite(rawServerPriceInPaise) || rawServerPriceInPaise <= 0);
    const currency = String(serverPlan.currency || tier.currency || "INR").toUpperCase();
    const durationDays = Number.isFinite(Number(serverPlan.durationDays))
      ? Number(serverPlan.durationDays)
      : Number(tier.durationDays || 30);
    const trialDurationMinutes = Number.isFinite(Number(serverPlan.trialDurationMinutes))
      ? Number(serverPlan.trialDurationMinutes)
      : Number(tier.trialDurationMinutes || 0);
    const serverPriceText = isContactSales
      ? "Contact Sales"
      : formatCatalogPrice(priceInPaise, currency, {
        allowFree: getPlanPublicId(tier.id) === "free",
      });
    const fallbackPriceLabel = String(serverPlan.priceLabel || "").trim();
    const priceLabel = fallbackPriceLabel
      || (hasPricingError ? (tier.id === "enterprise" ? "Custom pricing" : "Pricing unavailable") : serverPriceText);
    const features = Array.isArray(serverPlan.features)
      ? serverPlan.features.map((item) => String(item || "").trim()).filter(Boolean)
      : Array.isArray(tier.features)
        ? tier.features
        : [];
    const entitlements = normalizeEntitlements(serverPlan.entitlements);
    const tenantQuote = normalizeTenantQuote(serverPlan.tenantQuote);
    const rawSeatLimit = Object.prototype.hasOwnProperty.call(serverPlan, "seatLimit")
      ? serverPlan.seatLimit
      : Object.prototype.hasOwnProperty.call(serverPlan, "maxSystems")
        ? serverPlan.maxSystems
        : Object.prototype.hasOwnProperty.call(tier, "seatLimit")
          ? tier.seatLimit
          : Object.prototype.hasOwnProperty.call(tier, "maxSystems")
            ? tier.maxSystems
            : null;
    const seatLimit = rawSeatLimit === null
      ? null
      : Number.isFinite(Number(rawSeatLimit)) && Number(rawSeatLimit) > 0
        ? Number(rawSeatLimit)
        : null;

    const billingCaption = String(
      serverPlan.billing
      || tier.billing
      || (isContactSales ? "custom onboarding and pricing" : "")
    ).trim();
    const normalizedName = String(serverPlan.name || tier.name || "");
    const normalizedCta = String(serverPlan.cta || tier.cta || "");
    const fallbackBilling = hasPricingError
      ? "Fix plan pricing in admin before checkout"
      : (
        isContactSales
          ? "custom onboarding and pricing"
          : requiresPayment
            ? "/agent/month"
            : seatLimit === null
              ? "for unlimited agents"
              : `for up to ${seatLimit} agents`
      );
    const normalizedBilling = billingCaption || fallbackBilling;

    return applyPlanDisplayCopy({
      ...tier,
      name: normalizedName,
      description: String(serverPlan.description || tier.description || ""),
      priceInPaise,
      currency,
      requiresPayment,
      contactSales: isContactSales,
      durationDays,
      trialDurationMinutes,
      price: priceLabel,
      billing: normalizedBilling,
      features,
      entitlements,
      tenantQuote,
      seatLimit,
      maxSystems: seatLimit,
      pricingError: hasPricingError,
      cta: normalizedCta,
      featured: typeof serverPlan.featured === "boolean" ? serverPlan.featured : !!tier.featured,
    });
  });
}

function loadRazorpayCheckoutScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Browser environment not available"));
  }

  if (window.Razorpay) {
    return Promise.resolve(window.Razorpay);
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-razorpay-checkout="1"]');
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.Razorpay), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Razorpay checkout")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = RAZORPAY_CHECKOUT_SCRIPT_URL;
    script.async = true;
    script.dataset.razorpayCheckout = "1";
    script.onload = () => {
      if (window.Razorpay) {
        resolve(window.Razorpay);
      } else {
        reject(new Error("Razorpay checkout loaded without SDK"));
      }
    };
    script.onerror = () => reject(new Error("Unable to load Razorpay checkout script"));
    document.body.appendChild(script);
  });
}

export default function BillingPage() {
  const role = getRole();
  const canManageBilling = role === "admin" || role === "client";
  const navigate = useNavigate();
  const location = useLocation();

  // Instantly restore scroll position when returning from payment detail
  React.useEffect(() => {
    const savedY = location.state?.restoreScrollY;
    if (typeof savedY === "number") {
      // Use requestAnimationFrame to ensure DOM is painted before restoring
      requestAnimationFrame(() => window.scrollTo({ top: savedY, behavior: "instant" }));
      // Clear the state so a manual refresh doesn't re-scroll
      navigate(location.pathname, { replace: true, state: {} });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tiers, setTiers] = React.useState(() => mergeTiersWithServer([]));
  const [subscriptionsByPlan, setSubscriptionsByPlan] = React.useState({});
  const [currentSubscription, setCurrentSubscription] = React.useState(null);
  const [trialUsed, setTrialUsed] = React.useState(false);
  const [usedSeatCount, setUsedSeatCount] = React.useState(0);
  const [statusError, setStatusError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [processingPlanId, setProcessingPlanId] = React.useState("");
  const [paymentHistory, setPaymentHistory] = React.useState([]);
  const [historyStatusFilter, setHistoryStatusFilter] = React.useState("all");
  const [historyPage, setHistoryPage] = React.useState(1);
  const [accountProfile, setAccountProfile] = React.useState(() => ({
    fullName: sessionStorage.getItem("name") || "",
    email: sessionStorage.getItem("email") || "",
    phone: "",
    companyName: "",
  }));

  const [checkoutModalOpen, setCheckoutModalOpen] = React.useState(false);
  const [checkoutPlanId, setCheckoutPlanId] = React.useState("");
  const [checkoutAgentCount, setCheckoutAgentCount] = React.useState("");
  const [checkoutSource, setCheckoutSource] = React.useState("tier");
  const [checkoutSubmitting, setCheckoutSubmitting] = React.useState(false);
  const [checkoutError, setCheckoutError] = React.useState("");
  const [policyAcceptance, setPolicyAcceptance] = React.useState(false);
  const [freeTrialPromptOpen, setFreeTrialPromptOpen] = React.useState(false);
  const [freeTrialStarting, setFreeTrialStarting] = React.useState(false);

  // Proration / plan-change modal
  const [prorationModal, setProrationModal] = React.useState(null); // { tier } | null
  const [prorationCreditCtx, setProrationCreditCtx] = React.useState(null); // fetched once on open
  const [prorationLoading, setProrationLoading] = React.useState(false);
  const [prorationError, setProrationError] = React.useState("");
  const [prorationSubmitting, setProrationSubmitting] = React.useState(false);
  const [prorationAgentCount, setProrationAgentCount] = React.useState(1);

  const checkoutTier = React.useMemo(
    () => tiers.find((tier) => tier.id === checkoutPlanId) || null,
    [tiers, checkoutPlanId]
  );
  const activePlanId = React.useMemo(() => {
    const currentPlanId = getStoredPlanId(currentSubscription?.planId);
    if (currentPlanId) return currentPlanId;
    const fallbackActivePlanId = Object.values(subscriptionsByPlan).find((entry) => entry?.isActive)?.planId;
    return getStoredPlanId(fallbackActivePlanId);
  }, [currentSubscription?.planId, subscriptionsByPlan]);
  const activeTier = React.useMemo(
    () => tiers.find((tier) => getStoredPlanId(tier.id) === activePlanId) || null,
    [activePlanId, tiers]
  );
  const freeTier = React.useMemo(
    () => tiers.find((tier) => getPlanPublicId(tier.id) === "free") || null,
    [tiers]
  );
  const freeTrialSystemLimit = React.useMemo(() => {
    const parsedLimit = Number.parseInt(freeTier?.seatLimit || freeTier?.maxSystems, 10);
    return Number.isFinite(parsedLimit) && parsedLimit > 0 ? parsedLimit : 3;
  }, [freeTier?.maxSystems, freeTier?.seatLimit]);
  const hasAnyActivePlan = React.useMemo(() => {
    if (currentSubscription?.isActive) return true;
    return Object.values(subscriptionsByPlan).some((entry) => entry?.isActive);
  }, [currentSubscription?.isActive, subscriptionsByPlan]);
  const visibleTiers = React.useMemo(
    () => tiers.filter((tier) => getPlanPublicId(tier.id) !== "free"),
    [tiers]
  );
  const currentLicensedSeats = React.useMemo(() => {
    const seatCount = Number.parseInt(currentSubscription?.seatCount || currentSubscription?.maxSystems, 10);
    return Number.isFinite(seatCount) && seatCount > 0 ? seatCount : 0;
  }, [currentSubscription?.maxSystems, currentSubscription?.seatCount]);
  const currentRemainingPurchasableSeats = React.useMemo(() => {
    const rawValue = currentSubscription?.remainingPurchasableSeats;
    if (rawValue === null) return null;
    const parsed = Number.parseInt(rawValue, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }, [currentSubscription?.remainingPurchasableSeats]);
  const checkoutSeatCountLocked = false;
  const checkoutCurrentTierSubscription = React.useMemo(
    () => (checkoutTier ? subscriptionsByPlan[getStoredPlanId(checkoutTier.id)] || null : null),
    [checkoutTier, subscriptionsByPlan]
  );
  const checkoutStoredPlanId = checkoutTier ? getStoredPlanId(checkoutTier.id) : "";
  const checkoutPublicPlanId = checkoutTier ? getPlanPublicId(checkoutTier.id) : "";
  const checkoutIsEnterpriseTier = checkoutTier?.id === "enterprise";
  const checkoutIsFreeTier = checkoutPublicPlanId === "free";
  const checkoutIsRenewal = !!(
    checkoutTier
    && checkoutSource === "tier"
    && !checkoutIsEnterpriseTier
    && checkoutCurrentTierSubscription?.isActive
    && activePlanId === checkoutStoredPlanId
  );
  const checkoutIsInitialTierPurchase = !!(
    checkoutTier
    && checkoutSource === "tier"
    && !checkoutIsEnterpriseTier
    && !checkoutIsRenewal
  );
  const checkoutSeatLimit = checkoutTier?.seatLimit ?? null;
  const checkoutMinSelectableAgents = React.useMemo(() => {
    if (checkoutIsFreeTier) {
      const freeTrialLimit = Number.parseInt(checkoutTier?.seatLimit || checkoutTier?.maxSystems, 10);
      return Number.isFinite(freeTrialLimit) && freeTrialLimit > 0 ? freeTrialLimit : 3;
    }
    return 1;
  }, [checkoutIsFreeTier, checkoutTier?.maxSystems, checkoutTier?.seatLimit]);
  const checkoutMaxSelectableAgents = React.useMemo(() => {
    if (checkoutIsFreeTier) {
      const freeTrialLimit = Number.parseInt(checkoutTier?.seatLimit || checkoutTier?.maxSystems, 10);
      return Number.isFinite(freeTrialLimit) && freeTrialLimit > 0 ? freeTrialLimit : 3;
    }

    if (checkoutSource === "agents" && Number.isFinite(currentRemainingPurchasableSeats) && currentRemainingPurchasableSeats > 0) {
      return Math.max(1, currentRemainingPurchasableSeats);
    }

    const quotedSeatCount = Number.parseInt(checkoutTier?.tenantQuote?.seatCount, 10);
    if (checkoutIsEnterpriseTier && Number.isFinite(quotedSeatCount) && quotedSeatCount > 0) {
      return Math.max(1, quotedSeatCount);
    }

    if (Number.isFinite(checkoutSeatLimit) && checkoutSeatLimit > 0) {
      return Math.max(1, checkoutSeatLimit);
    }

    return MAX_AGENT_COUNT_ALLOWED;
  }, [
    checkoutIsEnterpriseTier,
    checkoutSeatLimit,
    checkoutSource,
    checkoutTier?.tenantQuote?.seatCount,
    currentRemainingPurchasableSeats,
  ]);
  const parsedCheckoutAgentCount = React.useMemo(
    () => parseAgentCount(checkoutAgentCount),
    [checkoutAgentCount]
  );
  const checkoutResolvedAgentCount = React.useMemo(
    () => clampAgentCount(parsedCheckoutAgentCount, checkoutMinSelectableAgents, checkoutMaxSelectableAgents),
    [checkoutMaxSelectableAgents, checkoutMinSelectableAgents, parsedCheckoutAgentCount]
  );
  const checkoutAgentFieldLabel = checkoutSource === "agents"
    ? "Additional Agents"
    : checkoutIsFreeTier
      ? "System Limit"
      : checkoutIsEnterpriseTier
        ? "Quoted Seat Count"
        : checkoutIsInitialTierPurchase
          ? "Initial Agent Count"
          : checkoutIsRenewal
            ? "Renewal Seat Count"
            : "Agent Count";
  const checkoutModalTitle = checkoutSource === "agents"
    ? "Purchase Agent Licenses"
    : checkoutIsFreeTier
      ? "Start Free Trial"
      : checkoutIsInitialTierPurchase
        ? `Choose Initial Agents For ${checkoutTier?.name || "Plan"}`
        : checkoutIsRenewal
          ? `Renew ${checkoutTier?.name || "Plan"}`
          : `${checkoutTier?.name || "Plan"} Plan`;
  const checkoutModalIntro = checkoutSource === "agents"
    ? "Select the additional seats to buy. Price shown is subtotal + 18% GST."
    : checkoutIsEnterpriseTier
      ? "Complete the selected plan purchase. Enterprise quote checkouts use the admin-issued locked seat count."
      : checkoutIsFreeTier
        ? `The free trial is completely free and limited to ${checkoutMinSelectableAgents} systems.`
        : checkoutIsInitialTierPurchase
          ? "Choose the initial number of agents. Price shown is calculated instantly — subtotal + 18% GST."
          : checkoutIsRenewal
            ? "Renew the plan with the current licensed seat count. Review the renewal bill below, then proceed to payment."
            : "Complete the selected plan purchase.";
  const enterpriseStateLabel = checkoutTier?.id !== "enterprise"
    ? ""
    : checkoutTier?.contactSales && !checkoutTier?.requiresPayment
      ? "Awaiting Quote"
      : checkoutTier?.requiresPayment && checkoutTier?.tenantQuote?.seatCount
        ? "Quote Active"
        : "Quote Pending";
  const checkoutSkipsSeatSelection = checkoutIsFreeTier || checkoutIsEnterpriseTier || checkoutIsRenewal || checkoutSeatCountLocked;
  const checkoutInputNote = !checkoutTier
    ? "Select a plan to review seat limits and pricing."
    : checkoutIsFreeTier
      ? `Free trial access is fixed at ${checkoutMinSelectableAgents} systems and cannot be increased.`
      : checkoutIsEnterpriseTier && checkoutTier.requiresPayment
        ? "Enterprise seat count is fixed by the active admin quote."
        : checkoutIsEnterpriseTier
          ? "Ask an admin to configure the enterprise quote before checkout."
          : checkoutSource === "agents"
            ? checkoutSeatLimit === null
              ? "Add as many seats as needed for the active plan."
              : `Additional seat purchase. Remaining purchasable seats on this plan: ${checkoutSeatLimit}.`
            : checkoutIsInitialTierPurchase
              ? checkoutSeatLimit === null
                ? "Choose the initial licensed seats for this subscription."
                : `Choose the initial licensed seats for this subscription. Maximum allowed: ${checkoutSeatLimit} agents.`
              : checkoutIsRenewal
                ? checkoutSeatLimit === null
                  ? "Renewal keeps the current plan active with the selected seat count."
                  : `Renewal can include up to ${checkoutSeatLimit} licensed agents.`
                : checkoutSeatLimit === null
                  ? "No seat cap for this plan."
                  : `Maximum allowed: ${checkoutSeatLimit} agents.`;
  const checkoutEmptyQuoteText = checkoutIsInitialTierPurchase
    ? checkoutIsEnterpriseTier
      ? "Enterprise amount will appear once the admin quote is available."
      : checkoutIsFreeTier
        ? `Free trial will activate with ${checkoutMinSelectableAgents} systems at no cost.`
        : "Select the initial agent count to calculate the total payable."
    : checkoutIsEnterpriseTier
      ? "Enterprise amount will appear once the admin quote is available."
      : "Select the agent count to calculate the total payable.";
  const checkoutUnitLabel = checkoutTier?.id === "free" ? "Trial Price" : "Per Agent (Base)";
  const checkoutTotalLabel = checkoutTier?.id === "free"
    ? "Total"
    : checkoutIsRenewal
      ? "Renewal Payable"
      : "Total Payable";
  const checkoutPoliciesAccepted = checkoutIsFreeTier || !!checkoutTier?.contactSales || policyAcceptance;

  // Proration seat-picker computed values
  const prorationIsEnterpriseTier = prorationModal?.tier?.id === "enterprise";
  const prorationEnterpriseQuotedSeats = React.useMemo(() => {
    if (!prorationIsEnterpriseTier) return null;
    const quoted = Number.parseInt(prorationModal?.tier?.tenantQuote?.seatCount, 10);
    if (Number.isFinite(quoted) && quoted > 0) return quoted;
    const fromSub = Number.parseInt(currentSubscription?.seatCount || currentSubscription?.maxSystems, 10);
    return Number.isFinite(fromSub) && fromSub > 0 ? fromSub : null;
  }, [prorationIsEnterpriseTier, prorationModal?.tier?.tenantQuote?.seatCount, currentSubscription?.seatCount, currentSubscription?.maxSystems]);
  const prorationMinSeats = Math.max(1, usedSeatCount);
  const prorationMaxSeats = React.useMemo(() => {
    if (prorationIsEnterpriseTier && prorationEnterpriseQuotedSeats) return prorationEnterpriseQuotedSeats;
    // Prefer seatLimit from the credit context (most accurate — server-confirmed)
    const ctxLimit = Number(prorationCreditCtx?.seatLimit);
    if (Number.isFinite(ctxLimit) && ctxLimit > 0) return Math.max(prorationMinSeats, ctxLimit);
    const tierLimit = Number(prorationModal?.tier?.seatLimit);
    if (Number.isFinite(tierLimit) && tierLimit > 0) return Math.max(prorationMinSeats, tierLimit);
    return Math.max(prorationMinSeats, 100);
  }, [prorationIsEnterpriseTier, prorationEnterpriseQuotedSeats, prorationCreditCtx?.seatLimit, prorationModal?.tier?.seatLimit, prorationMinSeats]);
  const prorationAdditionalSeats = Math.max(0, prorationAgentCount - usedSeatCount);
  const prorationCanAddMore = !prorationIsEnterpriseTier && prorationAgentCount < prorationMaxSeats;
  const prorationCanReduceMore = !prorationIsEnterpriseTier && prorationAgentCount > prorationMinSeats;

  // Locally-computed breakdown — recalculates instantly when agent count changes, no API call
  const prorationQuote = React.useMemo(() => {
    if (!prorationCreditCtx || !prorationAgentCount) return null;
    const { unitAmountInPaise, remainingCreditInPaise, direction, currency, currentPlanId, remainingDays, totalDays, totalPaidInPaise, newPlanSubtotalInPaise: serverSubtotal, newPlanTotalInPaise: serverTotal } = prorationCreditCtx;
    // Enterprise has flat quote pricing — use the server-computed amounts, never multiply by agent count
    const subtotal = prorationIsEnterpriseTier && serverSubtotal != null
      ? serverSubtotal
      : Math.round(unitAmountInPaise * prorationAgentCount);
    const tax = Math.round(subtotal * 0.18);
    const newPlanTotal = prorationIsEnterpriseTier && serverTotal != null
      ? serverTotal
      : subtotal + tax;
    // Downgrades never charge — mirror the change-plan backend rule
    const isDowngrade = direction === "downgrade";
    const creditApplied = isDowngrade ? newPlanTotal : Math.min(remainingCreditInPaise, newPlanTotal);
    const topUp = isDowngrade ? 0 : Math.max(0, newPlanTotal - creditApplied);
    return {
      direction,
      currentPlanId,
      remainingDays,
      totalDays,
      totalPaidInPaise,
      remainingCreditInPaise,
      newPlanSubtotalInPaise: subtotal,
      newPlanGstInPaise: tax,
      newPlanTotalInPaise: newPlanTotal,
      creditAppliedInPaise: creditApplied,
      topUpAmountInPaise: topUp,
      requiresPayment: topUp > 0,
      agentCount: prorationAgentCount,
      currency,
    };
  }, [prorationCreditCtx, prorationAgentCount, prorationIsEnterpriseTier]);

  const applySubscriptionPayload = React.useCallback((payload) => {
    const byPlan = payload?.subscriptionsByPlan && typeof payload.subscriptionsByPlan === "object"
      ? payload.subscriptionsByPlan
      : {};
    setSubscriptionsByPlan(byPlan);
    setCurrentSubscription(payload?.currentSubscription || null);
    setTrialUsed(!!payload?.trialUsed);
    setUsedSeatCount(Number(payload?.usedSeatCount || 0));
  }, []);

  const syncPostPurchaseAccessState = React.useCallback(async () => {
    try {
      const [featuresResponse, profileResponse] = await Promise.all([
        apiGet("/api/features"),
        apiGet("/api/auth/me"),
      ]);

      if (featuresResponse.ok) {
        const featuresPayload = await featuresResponse.json().catch(() => ({}));
        const unlockedMap = {};
        if (Array.isArray(featuresPayload?.unlockedFeatures)) {
          featuresPayload.unlockedFeatures.forEach((id) => {
            const key = String(id || "").trim();
            if (key) unlockedMap[key] = true;
          });
        }
        localStorage.setItem("unlockedFeatures", JSON.stringify(unlockedMap));
        window.dispatchEvent(
          new CustomEvent("features:updated", {
            detail: { unlockedFeatures: unlockedMap },
          })
        );
      }

      if (profileResponse.ok) {
        const profilePayload = await profileResponse.json().catch(() => null);
        if (profilePayload) {
          syncSessionProfile(profilePayload);
          window.dispatchEvent(new CustomEvent("session:profile-updated"));
        }
      }
    } catch {
      // Access refresh failure should not block successful subscription flow.
    }
  }, []);

  const refreshBillingHistory = React.useCallback(async () => {
    try {
      const paymentsResponse = await apiGet("/api/subscriptions/payments");

      if (paymentsResponse.ok) {
        const paymentsPayload = await paymentsResponse.json().catch(() => ({}));
        setPaymentHistory(Array.isArray(paymentsPayload?.payments) ? paymentsPayload.payments : []);
      }
    } catch {
      // Billing history refresh is non-blocking.
    }
  }, []);

  React.useEffect(() => {
    let isMounted = true;

    const fetchInitialData = async () => {
      try {
        const plansResponsePromise = apiGet("/api/subscriptions/plans/private")
          .then((response) => response)
          .catch(() => apiGet("/api/subscriptions/plans"));
        const [plansResponse, statusResponse] = await Promise.all([
          plansResponsePromise,
          apiGet("/api/subscriptions/status"),
        ]);

        if (!isMounted) return;

        if (plansResponse.ok) {
          const plansPayload = await plansResponse.json().catch(() => ({}));
          setTiers(mergeTiersWithServer(plansPayload?.plans));
        } else {
          setTiers(mergeTiersWithServer([]));
        }

        if (statusResponse.ok) {
          const statusPayload = await statusResponse.json().catch(() => ({}));
          applySubscriptionPayload(statusPayload);
          setStatusError("");
        } else {
          const errPayload = await statusResponse.json().catch(() => ({}));
          setStatusError(errPayload?.message || "Unable to load subscription status");
        }

        // Render the page as soon as critical billing data is ready.
        if (isMounted) setLoading(false);

        // Load non-critical data in the background — run all in parallel so none block each other.
        void (async () => {
          try {
            // Kick off payments + profile + access state all at once
            const [profileResponse, paymentsResponse] = await Promise.all([
              apiGet("/api/auth/me"),
              apiGet("/api/subscriptions/payments"),
            ]);
            if (!isMounted) return;

            if (profileResponse.ok) {
              const profile = await profileResponse.json().catch(() => ({}));
              const tenantName = typeof profile?.tenantId === "object"
                ? String(profile?.tenantId?.name || "").trim()
                : "";
              setAccountProfile({
                fullName: String(profile?.name || sessionStorage.getItem("name") || "").trim(),
                email: normalizeEmail(profile?.email || sessionStorage.getItem("email") || ""),
                phone: String(profile?.phone || "").trim(),
                companyName: String(profile?.companyName || tenantName || "").trim(),
              });
            }

            if (paymentsResponse.ok) {
              const paymentsPayload = await paymentsResponse.json().catch(() => ({}));
              if (isMounted) setPaymentHistory(Array.isArray(paymentsPayload?.payments) ? paymentsPayload.payments : []);
            } else {
              if (isMounted) setPaymentHistory([]);
            }
          } catch {
            // Ignore background enrichment failures.
          }
        })();

        // Run access state sync independently — do NOT block payment history rendering on it.
        void syncPostPurchaseAccessState();
      } catch {
        if (!isMounted) return;
        setStatusError("Unable to load billing data right now");
        if (isMounted) setLoading(false);
      } finally {
        // handled above to avoid waiting on non-critical network calls
      }
    };

    void fetchInitialData();
    return () => {
      isMounted = false;
    };
  }, [applySubscriptionPayload, syncPostPurchaseAccessState]);


  React.useEffect(() => {
    if (!checkoutModalOpen) return;

    const nextCount = String(checkoutResolvedAgentCount);
    if (nextCount !== String(checkoutAgentCount || "")) {
      setCheckoutAgentCount(nextCount);
    }
  }, [checkoutAgentCount, checkoutModalOpen, checkoutResolvedAgentCount]);

  const openCheckoutModal = React.useCallback((tier, { source = "tier" } = {}) => {
    if (!tier) return;
    const defaultCount = resolveCheckoutInitialAgentCount(tier, currentSubscription, source);
    setCheckoutPlanId(tier.id);
    setCheckoutSource(source);
    setCheckoutAgentCount(defaultCount);
    setPolicyAcceptance(false);
    setCheckoutModalOpen(true);
  }, [currentSubscription]);

  const closeCheckoutModal = React.useCallback(() => {
    if (checkoutSubmitting || !!processingPlanId) return;
    setCheckoutModalOpen(false);
    setCheckoutError("");
    setPolicyAcceptance(false);
  }, [checkoutSubmitting, processingPlanId]);

  const closeFreeTrialPrompt = React.useCallback(() => {
    if (freeTrialStarting || !!processingPlanId) return;
    setFreeTrialPromptOpen(false);
  }, [freeTrialStarting, processingPlanId]);

  const openSubscriptionModal = React.useCallback((tier) => {
    if (!tier) return;
    if (!canManageBilling) {
      toast.info("Subscription management is available to tenant owners only.");
      return;
    }
    if (tier.id === "free" && hasAnyActivePlan) {
      toast.info("Introductory trial unavailable while another subscription is active.");
      return;
    }
    if (tier.id === "free" && trialUsed) {
      const paidPlan = getDefaultPaidPlan(tiers);
      if (!paidPlan) {
        toast.info("Introductory trial unavailable for this workspace.");
        return;
      }
      toast.info(`Introductory trial unavailable. Continue with ${paidPlan.name} to activate a paid subscription.`);
      openCheckoutModal(paidPlan, { source: "tier" });
      return;
    }
    if (tier.id === "free") {
      setFreeTrialPromptOpen(true);
      return;
    }
    if (tier.id === "enterprise" && (tier.contactSales || !tier.requiresPayment)) {
      const subject = encodeURIComponent(`${tier.name} Plan Inquiry`);
      const body = encodeURIComponent(
        `Plan: ${tier.name}\nAccount email: ${normalizeEmail(accountProfile.email)}\nRequest: Please configure an enterprise quote for this tenant.`
      );
      window.location.href = `mailto:${SALES_EMAIL}?subject=${subject}&body=${body}`;
      return;
    }
    openCheckoutModal(tier, { source: "tier" });
  }, [
    accountProfile.email,
    canManageBilling,
    currentSubscription,
    hasAnyActivePlan,
    openCheckoutModal,
    tiers,
    trialUsed,
  ]);

  const openAgentPurchaseModal = React.useCallback(() => {
    if (!canManageBilling) {
      toast.info("Agent purchases are available to tenant owners only.");
      return;
    }

    const fallbackActivePlanId = Object.values(subscriptionsByPlan).find((entry) => entry?.isActive)?.planId;
    const activePlanId = getStoredPlanId(currentSubscription?.planId || fallbackActivePlanId);
    if (!activePlanId) {
      toast.info("No active subscription is currently assigned.");
      return;
    }

    let activeTier = tiers.find((tier) => getStoredPlanId(tier.id) === activePlanId) || null;
    if (activeTier?.id === "free") {
      const paidPlan = getDefaultPaidPlan(tiers);
      if (!paidPlan) {
        toast.info("The introductory trial is not renewable. Select a paid subscription to continue.");
        return;
      }
      activeTier = paidPlan;
      toast.info(`The introductory trial is not renewable. Continuing with ${paidPlan.name}.`);
    }
    if (!activeTier) {
      toast.info("The current subscription is not available for additional seat purchases.");
      return;
    }
    if (activeTier.id === "enterprise" && (!activeTier.requiresPayment || !activeTier.tenantQuote?.seatCount)) {
      toast.info("Enterprise seat purchases are controlled by the active quote.");
      return;
    }
    if (activeTier.seatLimit !== null && currentRemainingPurchasableSeats !== null && currentRemainingPurchasableSeats <= 0) {
      toast.info("No additional seats remain under the current plan limit.");
      return;
    }

    openCheckoutModal(activeTier, { source: "agents" });
  }, [canManageBilling, currentRemainingPurchasableSeats, currentSubscription?.planId, openCheckoutModal, subscriptionsByPlan, tiers]);

  const openRazorpayCheckout = React.useCallback(async (tier, orderData, customerDetails, selectedAgentCount) => {
    await loadRazorpayCheckoutScript();

    return new Promise((resolve) => {
      const razorpay = new window.Razorpay({
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.orderId,
        name: "VisuN AI Employee Monitoring",
        description: `${tier.name} Plan Subscription`,
        prefill: {
          name: customerDetails.fullName,
          email: customerDetails.email,
          contact: customerDetails.phone,
        },
        notes: {
          planId: getStoredPlanId(tier.id),
          planName: tier.name,
          companyName: customerDetails.companyName,
          billingAddress: customerDetails.billingAddress,
          teamSize: customerDetails.teamSize ? String(customerDetails.teamSize) : "",
          agentCount: String(selectedAgentCount),
          seatCount: String(selectedAgentCount),
          taxId: customerDetails.taxId || "",
        },
        theme: {
          color: "#1e8ef1",
        },
        handler: (response) => {
          resolve({ type: "success", payload: response });
        },
        modal: {
          ondismiss: () => resolve({ type: "dismissed" }),
        },
      });

      razorpay.on("payment.failed", (response) => {
        resolve({ type: "failed", payload: response });
      });

      razorpay.open();
    });
  }, []);

  const handleSubscribe = React.useCallback(async (tier, selectedAgentCount) => {
    if (!tier?.id || processingPlanId) return false;
    if (!Number.isFinite(selectedAgentCount) || selectedAgentCount <= 0) {
      toast.error("Enter a valid agent count.");
      return false;
    }

    const normalizedEmail = normalizeEmail(accountProfile.email || sessionStorage.getItem("email") || "");
    const normalizedFullName = String(accountProfile.fullName || sessionStorage.getItem("name") || "Account User").trim();
    const normalizedCompany = String(accountProfile.companyName || "Workspace").trim();
    const normalizedPhone = normalizePhoneInput(accountProfile.phone || "");

    if (!isValidEmailAddress(normalizedEmail)) {
      toast.error("A valid account email is required in the profile before checkout.");
      return false;
    }

    const customerDetails = {
      email: normalizedEmail,
      fullName: normalizedFullName || "Account User",
      phone: normalizedPhone,
      companyName: normalizedCompany,
      teamSize: selectedAgentCount,
      billingAddress: "",
      taxId: "",
    };

    setProcessingPlanId(tier.id);

    try {
      const storedPlanId = getStoredPlanId(tier.id);
      const publicPlanId = getPlanPublicId(tier.id);
      if (publicPlanId === "free") {
        const freeTrialLimit = Number.parseInt(tier?.seatLimit || tier?.maxSystems, 10);
        const fixedFreeTrialAgentCount = Number.isFinite(freeTrialLimit) && freeTrialLimit > 0 ? freeTrialLimit : 3;
        const freeResponse = await apiPost("/api/subscriptions/start-free", {
          ...customerDetails,
          agentCount: fixedFreeTrialAgentCount,
        });
        const freeData = await freeResponse.json().catch(() => ({}));
        if (!freeResponse.ok || !freeData?.success) {
          throw new Error(freeData?.message || "The introductory trial could not be activated.");
        }
        applySubscriptionPayload(freeData);
        await syncPostPurchaseAccessState();
        await refreshBillingHistory();
        toast.success(`${tier.name} activated successfully.`);
        return true;
      }
      if (tier.contactSales && !tier.requiresPayment) {
        throw new Error("Enterprise requires an active tenant quote before Razorpay checkout.");
      }

      const orderResponse = await apiPost("/api/subscriptions/create-order", {
        ...customerDetails,
        planId: storedPlanId,
        agentCount: selectedAgentCount,
        purchaseMode: checkoutSource,
        termsAccepted: policyAcceptance,
        refundPolicyAccepted: policyAcceptance,
        paymentPolicyAccepted: policyAcceptance,
      });
      const orderData = await orderResponse.json().catch(() => ({}));
      if (!orderResponse.ok || !orderData?.success) {
        const code = String(orderData?.errorCode || "").trim();
        if (code === "POLICY_NOT_ACCEPTED") {
          throw new Error("Please accept Terms, Refund Policy, and Payment Policy before continuing.");
        }
        throw new Error(orderData?.message || "Payment order could not be created.");
      }

      if (orderData.alreadyActive) {
        applySubscriptionPayload(orderData);
        await syncPostPurchaseAccessState();
        await refreshBillingHistory();
        toast.info(`${tier.name} is already active.`);
        return false;
      }

      const checkoutResult = await openRazorpayCheckout(tier, orderData, customerDetails, selectedAgentCount);
      if (checkoutResult.type === "dismissed") {
        toast.info("Payment cancelled.");
        await apiPost("/api/subscriptions/payment-failed", {
          orderId: orderData.orderId,
          message: "Payment cancelled by user (checkout modal dismissed)"
        }).catch(() => {});
        await refreshBillingHistory();
        return false;
      }
      if (checkoutResult.type === "failed") {
        const reason = checkoutResult?.payload?.error?.description || "Payment could not be completed.";
        await apiPost("/api/subscriptions/payment-failed", {
          orderId: orderData.orderId,
          message: reason
        }).catch(() => {});
        await refreshBillingHistory();
        throw new Error(reason);
      }

      const verifyResponse = await apiPost("/api/subscriptions/verify-payment", {
        planId: storedPlanId,
        orderId: orderData.orderId,
        razorpayPaymentId: checkoutResult?.payload?.razorpay_payment_id,
        razorpaySignature: checkoutResult?.payload?.razorpay_signature,
      });
      const verifyData = await verifyResponse.json().catch(() => ({}));
      if (!verifyResponse.ok || !verifyData?.success) {
        throw new Error(verifyData?.message || "Payment verification could not be completed.");
      }

      applySubscriptionPayload(verifyData);
      await syncPostPurchaseAccessState();
      await refreshBillingHistory();
      trackEvent("Purchase", {
        value: Number(orderData.amount || 0) / 100,
        currency: orderData.currency || "INR",
        content_name: tier.name,
      });
      toast.success(`${tier.name} activated successfully.`);
      return true;
    } catch (err) {
      const message = err?.message || "The subscription request could not be completed.";
      toast.error(message);
      return false;
    } finally {
      setProcessingPlanId("");
    }
  }, [
    accountProfile,
    applySubscriptionPayload,
    checkoutSource,
    openRazorpayCheckout,
    policyAcceptance,
    processingPlanId,
    refreshBillingHistory,
    syncPostPurchaseAccessState,
  ]);

  // Fetches credit context ONCE on modal open — never re-called on slider changes
  const fetchProrationCreditCtx = React.useCallback(async (tier, count) => {
    if (!tier) return;
    setProrationLoading(true);
    setProrationError("");
    setProrationCreditCtx(null);
    try {
      const stored = getStoredPlanId(tier.id);
      const res = await apiGet(`/api/subscriptions/prorate-preview?planId=${stored}&agentCount=${count}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data?.message || "Failed to load proration preview");
      setProrationCreditCtx({
        direction: data.direction,
        currentPlanId: data.currentPlanId,
        remainingDays: data.remainingDays,
        totalDays: data.totalDays,
        totalPaidInPaise: data.totalPaidInPaise,
        remainingCreditInPaise: data.remainingCreditInPaise,
        unitAmountInPaise: data.unitAmountInPaise,
        newPlanSubtotalInPaise: data.newPlanSubtotalInPaise ?? null,
        newPlanTotalInPaise: data.newPlanTotalInPaise ?? null,
        currency: data.currency,
        seatLimit: data.seatLimit ?? null,
      });
    } catch (err) {
      const rawMessage = String(err?.message || "Failed to calculate upgrade/downgrade estimate");
      const message = rawMessage
        .replace(/proposition/gi, "proration")
        .replace(/failed to load proration preview/gi, "Unable to load upgrade/downgrade estimate right now");
      setProrationError(message);
    } finally {
      setProrationLoading(false);
    }
  }, []);

  // Opens the proration modal seeded with the current licensed seat count
  const openProrationModal = React.useCallback(async (tier) => {
    if (!canManageBilling) { toast.info("Subscription management is available to tenant owners only."); return; }
    // Enterprise without an active quote → contact sales email
    if (tier?.id === "enterprise" && (tier?.contactSales || !tier?.requiresPayment)) {
      const subject = encodeURIComponent(`${tier.name} Plan Inquiry`);
      const body = encodeURIComponent(
        `Plan: ${tier.name}\nAccount email: ${normalizeEmail(accountProfile.email)}\nRequest: Please configure an enterprise quote for this tenant.`
      );
      window.location.href = `mailto:${SALES_EMAIL}?subject=${subject}&body=${body}`;
      return;
    }
    // Enterprise agent count is set by admin quote — never let user adjust it
    let initialCount;
    if (tier?.id === "enterprise") {
      const quotedSeats = Number.parseInt(tier?.tenantQuote?.seatCount, 10);
      const subSeats = Number.parseInt(currentSubscription?.seatCount || currentSubscription?.maxSystems, 10);
      initialCount = (Number.isFinite(quotedSeats) && quotedSeats > 0)
        ? quotedSeats
        : (Number.isFinite(subSeats) && subSeats > 0 ? subSeats : Math.max(1, usedSeatCount));
    } else {
      initialCount = Math.max(1, usedSeatCount);
    }
    setProrationAgentCount(initialCount);
    setProrationError("");
    setProrationCreditCtx(null);
    setProrationModal({ tier });
    await fetchProrationCreditCtx(tier, initialCount);
  }, [accountProfile.email, canManageBilling, currentSubscription?.maxSystems, currentSubscription?.seatCount, fetchProrationCreditCtx, usedSeatCount]);

  const closeProrationModal = React.useCallback(() => {
    if (prorationSubmitting) return;
    setProrationModal(null);
    setProrationCreditCtx(null);
    setProrationError("");
  }, [prorationSubmitting]);

  // Executes the upgrade/downgrade, calling change-plan then Razorpay if needed
  const handleChangePlan = React.useCallback(async () => {
    if (!prorationModal?.tier) return;
    const { tier } = prorationModal;
    const agentCount = prorationAgentCount;
    const stored = getStoredPlanId(tier.id);

    setProrationSubmitting(true);
    setProrationError("");
    try {
      const changeRes = await apiPost("/api/subscriptions/change-plan", {
        planId: stored,
        agentCount,
        termsAccepted: true,
        refundPolicyAccepted: true,
      });
      const changeData = await changeRes.json().catch(() => ({}));
      if (!changeRes.ok || !changeData.success) throw new Error(changeData?.message || "Plan change failed");

      if (!changeData.requiresPayment) {
        // Direct switch — no payment needed
        applySubscriptionPayload(changeData);
        await syncPostPurchaseAccessState();
        await refreshBillingHistory();
        toast.success(`Switched to ${tier.name} successfully.`);
        closeProrationModal();
        return;
      }

      // Payment required — open Razorpay
      await loadRazorpayCheckoutScript();
      const normalizedEmail = normalizeEmail(accountProfile.email || sessionStorage.getItem("email") || "");
      await new Promise((resolve) => {
        const rz = new window.Razorpay({
          key: changeData.keyId,
          amount: changeData.amount,
          currency: changeData.currency,
          order_id: changeData.orderId,
          name: "VisuN AI Employee Monitoring",
          description: `Plan change to ${tier.name}`,
          prefill: { name: accountProfile.fullName, email: normalizedEmail, contact: accountProfile.phone },
          theme: { color: "#1e8ef1" },
          handler: async (response) => {
            try {
              const verifyRes = await apiPost("/api/subscriptions/change-plan/verify", {
                purchaseId: changeData.purchaseId,
                orderId: changeData.orderId,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              });
              const verifyData = await verifyRes.json().catch(() => ({}));
              if (!verifyRes.ok || !verifyData.success) throw new Error(verifyData?.message || "Verification failed");
              applySubscriptionPayload(verifyData);
              await syncPostPurchaseAccessState();
              await refreshBillingHistory();
              trackEvent("Purchase", {
                value: Number(changeData.amount || 0) / 100,
                currency: changeData.currency || "INR",
                content_name: tier.name,
              });
              toast.success(`Switched to ${tier.name} successfully.`);
              closeProrationModal();
            } catch (err) {
              setProrationError(err?.message || "Payment verification failed");
            } finally {
              setProrationSubmitting(false);
              resolve();
            }
          },
          modal: {
            ondismiss: async () => {
              toast.info("Payment cancelled.");
              setProrationSubmitting(false);
              await apiPost("/api/subscriptions/payment-failed", {
                orderId: changeData.orderId,
                message: "Payment cancelled by user (checkout modal dismissed)"
              }).catch(() => {});
              await refreshBillingHistory();
              resolve();
            },
          },
        });
        rz.on("payment.failed", async (response) => {
          const reason = response?.error?.description || "Payment failed. Please try again.";
          setProrationError(reason);
          setProrationSubmitting(false);
          await apiPost("/api/subscriptions/payment-failed", {
            orderId: changeData.orderId,
            message: reason
          }).catch(() => {});
          await refreshBillingHistory();
          resolve();
        });
        rz.open();
      });
    } catch (err) {
      setProrationError(err?.message || "Plan change failed");
      setProrationSubmitting(false);
    }
  }, [prorationModal, prorationAgentCount, applySubscriptionPayload, syncPostPurchaseAccessState, refreshBillingHistory, accountProfile, closeProrationModal]);

  const handleFreeTrialConfirm = React.useCallback(async () => {
    if (!freeTier || freeTrialStarting || !!processingPlanId) return;
    setFreeTrialStarting(true);
    const activated = await handleSubscribe(freeTier, freeTrialSystemLimit);
    if (activated) {
      setFreeTrialPromptOpen(false);
    }
    setFreeTrialStarting(false);
  }, [freeTier, freeTrialStarting, freeTrialSystemLimit, handleSubscribe, processingPlanId]);

  const handleCheckoutConfirm = React.useCallback(async () => {
    if (!checkoutTier) return;
    const resolvedAgentCount = checkoutResolvedAgentCount || parseAgentCount(checkoutAgentCount);
    if (!resolvedAgentCount) {
      setCheckoutError(
        checkoutIsInitialTierPurchase
          ? "Choose the initial number of agents to continue."
          : "Enter a valid agent count."
      );
      return;
    }

    if (!checkoutIsFreeTier && !checkoutTier.contactSales && !checkoutPoliciesAccepted) {
      setCheckoutError("Please accept the Terms & Conditions to proceed.");
      return;
    }

    if (checkoutTier.contactSales && !checkoutTier.requiresPayment) {
      const subject = encodeURIComponent(`${checkoutTier.name} Plan Inquiry`);
      const body = encodeURIComponent(
        `Plan: ${checkoutTier.name}\nAgents needed: ${resolvedAgentCount}\nAccount email: ${normalizeEmail(accountProfile.email)}`
      );
      window.location.href = `mailto:${SALES_EMAIL}?subject=${subject}&body=${body}`;
      closeCheckoutModal();
      return;
    }

    setCheckoutSubmitting(true);
    const success = await handleSubscribe(checkoutTier, resolvedAgentCount);
    setCheckoutSubmitting(false);
    if (success) {
      closeCheckoutModal();
    }
  }, [
    accountProfile.email,
    checkoutAgentCount,
    checkoutIsInitialTierPurchase,
    checkoutIsFreeTier,
    checkoutPoliciesAccepted,
    checkoutResolvedAgentCount,
    checkoutTier,
    closeCheckoutModal,
    handleSubscribe,
  ]);

  const frontendQuote = React.useMemo(() => {
    if (!checkoutTier || !parsedCheckoutAgentCount || parsedCheckoutAgentCount <= 0) return null;
    if (checkoutIsFreeTier) {
      return { agentCount: checkoutResolvedAgentCount, unitAmountInPaise: 0, subtotalAmountInPaise: 0, taxAmountInPaise: 0, totalAmountInPaise: 0, currency: checkoutTier.currency || "INR" };
    }

    let subtotal = 0;
    let unitPaise = 0;
    const publicId = getPlanPublicId(checkoutTier.id);

    if (checkoutSource === "agents") {
      unitPaise = publicId === "essentials" ? 10000 : 15000;
      subtotal = unitPaise * checkoutResolvedAgentCount;
    } else {
      if (publicId === "enterprise") {
        subtotal = Number(checkoutTier.priceInPaise) || 0;
        unitPaise = subtotal / checkoutResolvedAgentCount;
      } else {
        unitPaise = Number(checkoutTier.priceInPaise) || 0;
        subtotal = unitPaise * checkoutResolvedAgentCount;
      }
    }

    const tax = Math.round(subtotal * 0.18);
    return { agentCount: checkoutResolvedAgentCount, unitAmountInPaise: unitPaise, subtotalAmountInPaise: subtotal, taxAmountInPaise: tax, totalAmountInPaise: subtotal + tax, currency: checkoutTier.currency || "INR" };
  }, [checkoutTier, parsedCheckoutAgentCount, checkoutResolvedAgentCount, checkoutIsFreeTier, checkoutSource]);

  const checkoutBlocked = !checkoutTier
    || !parsedCheckoutAgentCount
    || checkoutSubmitting
    || !!processingPlanId
    || (!checkoutIsFreeTier && !checkoutTier?.contactSales && !frontendQuote)
    || (!checkoutIsFreeTier && !checkoutTier?.contactSales && !checkoutPoliciesAccepted);
  const checkoutActionLabel = checkoutSource === "agents"
    ? "Purchase Seats"
    : checkoutTier?.contactSales && !checkoutTier?.requiresPayment
      ? "Contact Sales"
      : checkoutIsFreeTier
        ? "Claim"
        : checkoutIsRenewal
          ? "Pay Renewal"
          : "Proceed to Pay";

  // Auto-open checkout when navigated back from plan detail page with ?autoCheckout=planId
  React.useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const autoId = params.get("autoCheckout");
    if (!autoId) return;
    const tier = tiers.find(t => t.id === autoId);
    if (tier) openSubscriptionModal(tier);
    navigate("/billing", { replace: true });
  }, [loading, location.search]);

  // Distinct statuses actually present in the fetched history — the backend
  // only ever returns succeeded/failed today (see /api/subscriptions/payments),
  // but deriving this from real data instead of hardcoding both options keeps
  // it correct if that changes rather than showing a filter option that can
  // never match anything.
  const historyStatusOptions = React.useMemo(
    () => Array.from(new Set(paymentHistory.map((r) => String(r.status || "").toLowerCase()).filter(Boolean))),
    [paymentHistory]
  );
  const filteredHistory = historyStatusFilter === "all"
    ? paymentHistory
    : paymentHistory.filter((r) => String(r.status || "").toLowerCase() === historyStatusFilter);
  const historyTotalPages = Math.max(1, Math.ceil(filteredHistory.length / HISTORY_PAGE_SIZE));
  const pagedHistory = filteredHistory.slice((historyPage - 1) * HISTORY_PAGE_SIZE, historyPage * HISTORY_PAGE_SIZE);

  return (
    <div className="billing-page">
      {statusError ? <p className="billing-error-banner">{statusError}</p> : null}
      <section className="billing-premium-summary">
        <div className="billing-summary-content">
          <div className="billing-summary-header">
            <h2>Current Subscription</h2>
            <span className={`billing-status-badge ${currentSubscription?.isActive ? 'active' : 'inactive'}`}>
              {currentSubscription?.isActive ? "Active" : "Inactive"}
            </span>
          </div>

          <div className="billing-summary-main">
            <div className="billing-summary-tier">
              <h3>{activeTier?.name || "No active plan"}</h3>
              <p>{currentSubscription?.isActive ? `Renews on ${formatExpiryDate(currentSubscription?.expiresAt)}` : "Please choose a plan to continue."}</p>
            </div>

            <div className="billing-summary-stats">
              <div className="stat-item">
                <span className="stat-value">{Number(currentSubscription?.usedSeatCount || usedSeatCount || 0)} <span className="stat-divider">/</span> {currentLicensedSeats || 0}</span>
                <span className="stat-label">Seats In Use</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{currentRemainingPurchasableSeats === null ? "Unlimited" : currentRemainingPurchasableSeats ?? "-"}</span>
                <span className="stat-label">Remaining Allowed</span>
              </div>
            </div>
          </div>

          <div className="billing-summary-footer">
            <p>Manage your billing, add extra seats, and upgrade your experience.</p>
            {canManageBilling && (
              <button
                type="button"
                className="billing-action-outline"
                onClick={openAgentPurchaseModal}
                disabled={loading || !!processingPlanId || checkoutSubmitting}
              >
                Purchase Extra Seats
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ── Plans table ── */}
      <section className="bp-section">
        <div className="bp-section-head">
          <div>
            <h2 className="bp-section-title">Plans</h2>
            <p className="bp-section-sub">Click a plan to view features and manage your subscription.</p>
          </div>
        </div>

        <div className="billing-grid">
          {visibleTiers.map((tier) => {
            const subscription = subscriptionsByPlan[getStoredPlanId(tier.id)] || null;
            const isActive = !!subscription?.isActive;
            const storedTierId = getStoredPlanId(tier.id);
            const tierPriority = TIER_PRIORITY[storedTierId] || TIER_PRIORITY[tier.id] || 0;
            const currentPriority = TIER_PRIORITY[activePlanId] || 0;
            const isCurrentPlan = isActive && activePlanId === storedTierId;

            // Upgrade / downgrade direction when another plan is active
            let direction = null; // "upgrade" | "downgrade" | null
            if (hasAnyActivePlan && !isCurrentPlan && getPlanPublicId(tier.id) !== "free") {
              direction = tierPriority > currentPriority ? "upgrade" : "downgrade";
            }

            // Downgrade blocked if active agent count exceeds new tier seat limit
            const seatLimit = tier.seatLimit ?? null;
            const agentCountBlocksDowngrade =
              direction === "downgrade" &&
              seatLimit !== null &&
              usedSeatCount > seatLimit;

            // Show "Claim Free Trial" secondary on this tier when free trial unused and no active plan
            const showClaimFreeTrialHere = !trialUsed && !hasAnyActivePlan && getPlanPublicId(tier.id) === "pro";

            // Main CTA label
            let ctaLabel = tier.cta || "Subscribe Now";
            if (isCurrentPlan) ctaLabel = "Subscribed";
            else if (tier.id === "enterprise" && (tier.contactSales || !tier.requiresPayment)) ctaLabel = "Contact Sales";
            else if (direction === "upgrade") ctaLabel = "Upgrade";
            else if (direction === "downgrade") ctaLabel = "Downgrade";

            const planPublicId = getPlanPublicId(tier.id);
            const planIcon = planPublicId === "free" ? <Gift size={17} />
              : planPublicId === "essentials" ? <Shield size={17} />
              : planPublicId === "pro" ? <Zap size={17} />
              : <Building2 size={17} />;

            return (
              <div key={tier.id} className={`billing-card${tier.featured ? " billing-card--featured" : ""}${isActive ? " billing-card--active" : ""}`}>
                {isActive && <span className="billing-badge billing-badge--active">Active</span>}
                {tier.featured && !isActive && <span className="billing-badge">Popular</span>}
                <span className={`billing-tier-icon billing-tier-icon--${planPublicId}`}>{planIcon}</span>
                <h3>{tier.name}</h3>
                <p className="billing-desc">{tier.description}</p>
                <div className="billing-price">
                  <span>{tier.price}</span>
                  <small>{tier.billing}</small>
                </div>
                {tier.seatLimit && (
                  <p className="billing-seat-limit">Up to {tier.seatLimit} agents</p>
                )}
                <ul className="billing-feature-list">
                  {tier.features.map((f) => (
                    <li key={f}><Check size={15} />{f}</li>
                  ))}
                </ul>

                {/* Agent-count blocks downgrade → Not Applicable */}
                {agentCountBlocksDowngrade ? (
                  <div className="billing-not-applicable">
                    <span>Not Applicable</span>
                    <span className="billing-na-info" title={`You have ${usedSeatCount} active agents but this plan supports only ${seatLimit}. Block excess agents first to downgrade.`}>
                      <Info size={13} />
                    </span>
                  </div>
                ) : isCurrentPlan ? (
                  <button
                    type="button"
                    className="billing-action"
                    disabled
                    title="This plan is already active"
                  >
                    {ctaLabel}
                  </button>
                ) : direction ? (
                  /* Upgrade / Downgrade via proration modal */
                  <button
                    type="button"
                    className={`billing-action${direction === "downgrade" ? " billing-action--downgrade" : " billing-action--upgrade"}`}
                    onClick={() => openProrationModal(tier)}
                    disabled={loading || prorationLoading || prorationSubmitting || !!processingPlanId}
                  >
                    {prorationLoading ? "Loading…" : ctaLabel}
                  </button>
                ) : (
                  /* New subscription / Manage / Enterprise */
                  <button
                    type="button"
                    className="billing-action"
                    onClick={() => openSubscriptionModal(tier)}
                    disabled={loading || !!processingPlanId || checkoutSubmitting}
                  >
                    {ctaLabel}
                  </button>
                )}

                {/* Free trial claim button on Pro tier */}
                {showClaimFreeTrialHere && (
                  <button
                    type="button"
                    className="billing-action billing-action--claim billing-action--secondary"
                    onClick={() => openSubscriptionModal(freeTier)}
                    disabled={loading || !!processingPlanId}
                    style={{ marginTop: 8 }}
                  >
                    Claim Free Trial
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Payment history table ── */}
      <section className="bp-section" id="payment-history">
        <div className="bp-section-head">
          <div>
            <h2 className="bp-section-title">Payment History</h2>
            <p className="bp-section-sub">Click a record to view full payment details.</p>
          </div>
          {paymentHistory.length > 0 && historyStatusOptions.length > 1 && (
            <PillSelect
              value={historyStatusFilter}
              onChange={(e) => { setHistoryStatusFilter(e.target.value); setHistoryPage(1); }}
            >
              <option value="all">All Statuses</option>
              {historyStatusOptions.map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </PillSelect>
          )}
        </div>

        {paymentHistory.length === 0 ? (
          <p className="bp-empty">No payment records yet.</p>
        ) : filteredHistory.length === 0 ? (
          <p className="bp-empty">No payments match this filter.</p>
        ) : (
          <div className="bp-table-wrap">
            <table className="bp-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {pagedHistory.map((row) => (
                  <tr
                    key={row.id}
                    className="bp-row"
                    onClick={() => navigate(`/billing/payment/${row.id}`, { state: { record: row, scrollY: window.scrollY } })}
                  >
                    <td>
                      {row.purchaseMode === "agents" || row.purchaseKind === "agent_addon"
                        ? `${getPlanDisplayName(row.planId, row.planName)} (Add-on: +${row.agentCount || 0} ${row.agentCount === 1 ? "Agent" : "Agents"})`
                        : (row.purchaseKind === "plan_change_direct" || row.purchaseKind === "plan_change") && row.fromPlanId && row.fromPlanId !== row.planId
                          ? `${getPlanDisplayName(row.fromPlanId, row.fromPlanName || row.fromPlanId)} → ${getPlanDisplayName(row.planId, row.planName)} (${row.direction === "downgrade" ? "Downgrade" : "Upgrade"})`
                          : getPlanDisplayName(row.planId, row.planName) || "-"}
                    </td>
                    <td>{formatPrice(row.totalAmount || row.amount, row.currency)}</td>
                    <td>
                      <span className={`bp-badge ${
                        row.status === "succeeded" || row.status === "paid"
                          ? "bp-badge--success"
                          : row.status === "failed"
                          ? "bp-badge--failed"
                          : "bp-badge--pending"
                      }`}>
                        {row.status || "-"}
                      </span>
                    </td>
                    <td className="bp-muted">
                      {row.status === "succeeded" || row.status === "paid"
                        ? (row.paidAt ? formatDateTime(row.paidAt) : formatDateTime(row.createdAt)) || "-"
                        : row.status === "failed"
                        ? (formatDateTime(row.updatedAt || row.createdAt) || "-")
                        : formatDateTime(row.createdAt) || "-"
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="devices-pagination">
              <span className="devices-pagination-count">
                {(historyPage - 1) * HISTORY_PAGE_SIZE + 1}–{Math.min(historyPage * HISTORY_PAGE_SIZE, filteredHistory.length)} of {filteredHistory.length} record{filteredHistory.length !== 1 ? "s" : ""}
              </span>
              <div className="devices-pagination-controls">
                <button
                  className="gc-icon-btn"
                  data-tip="Previous page"
                  onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                  disabled={historyPage <= 1}
                >
                  <ChevronLeft size={15} />
                </button>
                <span className="devices-pagination-page">{historyPage} / {historyTotalPages}</span>
                <button
                  className="gc-icon-btn"
                  data-tip="Next page"
                  onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                  disabled={historyPage >= historyTotalPages}
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {freeTrialPromptOpen && freeTier ? (
        <div
          className="billing-modal-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeFreeTrialPrompt();
            }
          }}
        >
          <div className="billing-modal billing-modal--compact">
            <h3>Start Free Trial</h3>
            <p>
              Activates the introductory trial immediately. Complimentary access includes up to {freeTrialSystemLimit} monitored systems.
            </p>
            <div className="billing-modal-actions billing-modal-actions--compact">
              <button
                type="button"
                className="billing-modal-cancel"
                onClick={closeFreeTrialPrompt}
                disabled={freeTrialStarting || !!processingPlanId}
              >
                Not Now
              </button>
              <button
                type="button"
                className="billing-modal-confirm"
                style={{ background: "linear-gradient(135deg, #1fa968 0%, #36c97f 100%)" }}
                onClick={() => {
                  void handleFreeTrialConfirm();
                }}
                disabled={freeTrialStarting || !!processingPlanId}
              >
                {freeTrialStarting || !!processingPlanId ? "Starting..." : "OK"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── Proration / Plan-Change Modal ── */}
      {(prorationModal || prorationLoading) && (
        <div
          className="billing-modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) closeProrationModal(); }}
        >
          <div className="billing-modal billing-modal--proration">
            {(() => {
              const tier = prorationModal?.tier;
              const q = prorationQuote; // locally computed — always in sync, no API round-trip
              const isUpgrade = prorationCreditCtx?.direction === "upgrade";
              const maxAdditional = prorationMaxSeats - prorationMinSeats;
              const occupiedPct = prorationMaxSeats > 0 ? Math.min(100, (usedSeatCount / prorationMaxSeats) * 100) : 0;
              const additionalPct = prorationMaxSeats > 0 ? Math.min(100 - occupiedPct, (prorationAdditionalSeats / prorationMaxSeats) * 100) : 0;
              const currency = prorationCreditCtx?.currency ?? "INR";

              return (
                <>
                  <h3>
                    {prorationCreditCtx
                      ? isUpgrade ? "⬆ Upgrade" : "⬇ Downgrade"
                      : "Change Plan"}
                    {tier ? ` to ${tier.name}` : ""}
                  </h3>

                  {/* ── Seat picker (always visible, no loading state) ── */}
                  <div className="billing-seat-picker">
                    <div className="billing-seat-breakdown">
                      <div className="billing-seat-breakdown__bar">
                        <div className="billing-seat-breakdown__occupied" style={{ width: `${occupiedPct}%` }} title={`${usedSeatCount} currently licensed (locked)`} />
                        <div className="billing-seat-breakdown__additional" style={{ width: `${additionalPct}%` }} title={`${prorationAdditionalSeats} additional`} />
                      </div>
                      <div className="billing-seat-breakdown__labels">
                        <span className="billing-seat-occupied-label">
                          <span className="billing-seat-dot billing-seat-dot--occupied" />
                          {usedSeatCount} occupied
                        </span>
                        <span className="billing-seat-sep">+</span>
                        <span className="billing-seat-additional-label">
                          <span className="billing-seat-dot billing-seat-dot--additional" />
                          {prorationAdditionalSeats} additional
                        </span>
                        <span className="billing-seat-sep">=</span>
                        <strong className="billing-seat-total-label">{prorationAgentCount} total</strong>
                      </div>
                    </div>

                    {prorationIsEnterpriseTier ? null : (
                      <div className="billing-agent-picker__controls">
                        <button
                          type="button"
                          className="billing-agent-picker__step"
                          onClick={() => setProrationAgentCount((n) => Math.max(prorationMinSeats, n - 1))}
                          disabled={prorationLoading || prorationSubmitting || !prorationCanReduceMore}
                          aria-label="Remove one seat"
                        >
                          <Minus size={16} />
                        </button>
                        <input
                          type="range"
                          min={0}
                          max={maxAdditional}
                          step={1}
                          value={prorationAdditionalSeats}
                          onChange={(e) => setProrationAgentCount(prorationMinSeats + Number(e.target.value))}
                          disabled={prorationLoading || prorationSubmitting || maxAdditional <= 0}
                          className="billing-agent-picker__range billing-agent-picker__range--proration"
                          aria-label="Select total seat count"
                        />
                        <button
                          type="button"
                          className="billing-agent-picker__step"
                          onClick={() => setProrationAgentCount((n) => Math.min(prorationMaxSeats, n + 1))}
                          disabled={prorationLoading || prorationSubmitting || !prorationCanAddMore}
                          aria-label="Add one seat"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    )}
                    <p className="billing-input-note">
                      {prorationIsEnterpriseTier
                        ? prorationEnterpriseQuotedSeats
                          ? `Seat count fixed at ${prorationEnterpriseQuotedSeats} by admin quote — contact admin to adjust.`
                          : "Seat count is controlled by admin — contact admin to configure the enterprise quote."
                        : usedSeatCount > 0
                          ? `${usedSeatCount} seat${usedSeatCount !== 1 ? "s" : ""} currently licensed — included as minimum.`
                          : "Select the total number of agent seats for the new plan."}
                      {!prorationIsEnterpriseTier && prorationMaxSeats < MAX_AGENT_COUNT_ALLOWED ? ` Max for this plan: ${prorationMaxSeats}.` : ""}
                    </p>
                  </div>

                  {/* ── Breakdown (stable structure — skeletons while loading, values when ready) ── */}
                  <div className={`billing-quote-box billing-proration-breakdown${prorationLoading ? " billing-proration-breakdown--loading" : ""}`}>
                    {prorationLoading ? (
                      <>
                        {[...Array(7)].map((_, i) => (
                          <div key={i} className="billing-quote-row billing-quote-row--skeleton">
                            <span className="billing-skeleton-line" style={{ width: `${45 + (i % 3) * 15}%` }} />
                            <span className="billing-skeleton-line" style={{ width: "22%" }} />
                          </div>
                        ))}
                      </>
                    ) : q ? (
                      <>
                        <div className="billing-quote-row billing-quote-row--muted">
                          <span>Current Plan</span>
                          <strong style={{ textTransform: "capitalize" }}>{q.currentPlanId}</strong>
                        </div>
                        <div className="billing-quote-row billing-quote-row--muted">
                          <span>New Plan</span>
                          <strong>{tier?.name}</strong>
                        </div>
                        <div className="billing-quote-row billing-quote-row--muted">
                          <span>Days Remaining on Current</span>
                          <strong>{q.remainingDays} / {q.totalDays} days</strong>
                        </div>
                        <div className="billing-quote-row billing-quote-row--muted">
                          <span>Amount Paid (Current)</span>
                          <strong>{formatPrice(q.totalPaidInPaise, currency)}</strong>
                        </div>
                        <div className="billing-quote-row">
                          <span>Remaining Credit</span>
                          <strong style={{ color: "#36b37e" }}>{formatPrice(q.remainingCreditInPaise, currency)}</strong>
                        </div>
                        <div className="billing-quote-row" style={{ borderTop: "1px solid var(--border)", marginTop: 6, paddingTop: 6 }}>
                          <span>New Plan Base ({q.agentCount} agents)</span>
                          <strong>{formatPrice(q.newPlanSubtotalInPaise, currency)}</strong>
                        </div>
                        <div className="billing-quote-row">
                          <span>GST (18%)</span>
                          <strong>{formatPrice(q.newPlanGstInPaise, currency)}</strong>
                        </div>
                        <div className="billing-quote-row">
                          <span>New Plan Total</span>
                          <strong>{formatPrice(q.newPlanTotalInPaise, currency)}</strong>
                        </div>
                        <div className="billing-quote-row">
                          <span>Credit Applied</span>
                          <strong style={{ color: "#36b37e" }}>− {formatPrice(q.creditAppliedInPaise, currency)}</strong>
                        </div>
                        <div className="billing-quote-row billing-quote-row--total">
                          <span>{q.requiresPayment ? "Amount to Pay Now" : "Amount Due"}</span>
                          <strong>{q.requiresPayment ? formatPrice(q.topUpAmountInPaise, currency) : "Free"}</strong>
                        </div>
                      </>
                    ) : prorationError ? null : (
                      /* placeholder rows so modal height stays stable */
                      <>
                        {[...Array(7)].map((_, i) => (
                          <div key={i} className="billing-quote-row billing-quote-row--skeleton">
                            <span className="billing-skeleton-line" style={{ width: `${45 + (i % 3) * 15}%` }} />
                            <span className="billing-skeleton-line" style={{ width: "22%" }} />
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  {!prorationLoading && q && !q.requiresPayment && (
                    <p style={{ fontSize: "0.8rem", color: "#36b37e", marginTop: 8 }}>
                      Your remaining credit covers the full cost — no payment needed.
                    </p>
                  )}

                  {prorationError && (
                    <div style={{ marginTop: 8 }}>
                      <p className="billing-modal-error">{prorationError}</p>
                      <button
                        type="button"
                        style={{ fontSize: "0.8rem", color: "var(--accent-primary)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                        onClick={() => void fetchProrationCreditCtx(tier, prorationAgentCount)}
                      >
                        Retry
                      </button>
                    </div>
                  )}

                  <div className="billing-modal-actions">
                    <button type="button" className="billing-modal-cancel" onClick={closeProrationModal} disabled={prorationSubmitting}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="billing-modal-confirm"
                      onClick={() => { void handleChangePlan(); }}
                      disabled={prorationSubmitting || prorationLoading || !q}
                    >
                      {prorationSubmitting
                        ? "Processing…"
                        : prorationLoading
                          ? "Loading…"
                          : !q
                            ? "Calculating…"
                            : q.requiresPayment
                              ? `Pay ${formatPrice(q.topUpAmountInPaise, currency)}`
                              : `Confirm ${isUpgrade ? "Upgrade" : "Downgrade"}`}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {checkoutModalOpen && checkoutTier ? (
        <div
          className="billing-modal-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeCheckoutModal();
            }
          }}
        >
          <div className="billing-modal">
            <h3>{checkoutModalTitle}</h3>
            <p>{checkoutModalIntro}</p>
            {checkoutIsEnterpriseTier ? (
              <div className="billing-input-note" style={{ marginBottom: 10 }}>
                <strong>Enterprise Status:</strong> {enterpriseStateLabel}
              </div>
            ) : null}

            <div className="billing-form-grid">
              <label>
                {checkoutAgentFieldLabel}
                <div className="billing-agent-picker">
                  <div className="billing-agent-picker__summary">
                    <strong>{checkoutResolvedAgentCount.toLocaleString("en-IN")}</strong>
                    <span>
                      {checkoutSkipsSeatSelection
                        ? checkoutIsFreeTier
                          ? `Fixed at ${checkoutResolvedAgentCount.toLocaleString("en-IN")} systems for the free trial`
                          : checkoutSeatCountLocked
                            ? checkoutIsRenewal
                              ? "Renewal uses your current licensed seat count"
                              : "Fixed by active subscription or quote"
                            : checkoutIsEnterpriseTier
                              ? checkoutTier?.tenantQuote?.seatCount
                                ? "Fixed by admin quote — contact admin to adjust"
                                : "Seat count is set by admin — contact admin to configure"
                              : "Renewal uses your current licensed count"
                        : `${checkoutMinSelectableAgents.toLocaleString("en-IN")} to ${checkoutMaxSelectableAgents.toLocaleString("en-IN")} agents`}
                    </span>
                  </div>
                  {!checkoutSkipsSeatSelection ? (
                    <div className="billing-agent-picker__controls">
                      <button
                        type="button"
                        className="billing-agent-picker__step"
                        onClick={() => {
                          setCheckoutAgentCount(String(
                            Math.max(checkoutMinSelectableAgents, checkoutResolvedAgentCount - 1)
                          ));
                        }}
                        disabled={
                          checkoutSubmitting
                          || checkoutSubmitting
                          || !!processingPlanId
                          || checkoutResolvedAgentCount <= checkoutMinSelectableAgents
                        }
                        aria-label="Decrease agent count"
                      >
                        <Minus size={16} />
                      </button>
                      <input
                        type="range"
                        min={checkoutMinSelectableAgents}
                        max={checkoutMaxSelectableAgents}
                        step="1"
                        value={checkoutResolvedAgentCount}
                        onChange={(event) => {
                          setCheckoutAgentCount(String(clampAgentCount(
                            event.target.value,
                            checkoutMinSelectableAgents,
                            checkoutMaxSelectableAgents
                          )));
                        }}
                        disabled={checkoutSubmitting || checkoutSubmitting || !!processingPlanId}
                        aria-label={checkoutAgentFieldLabel}
                        className="billing-agent-picker__range"
                      />
                      <button
                        type="button"
                        className="billing-agent-picker__step"
                        onClick={() => {
                          setCheckoutAgentCount(String(
                            Math.min(checkoutMaxSelectableAgents, checkoutResolvedAgentCount + 1)
                          ));
                        }}
                        disabled={
                          checkoutSubmitting
                          || checkoutSubmitting
                          || !!processingPlanId
                          || checkoutResolvedAgentCount >= checkoutMaxSelectableAgents
                        }
                        aria-label="Increase agent count"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  ) : null}
                </div>
                <span className="billing-input-note">
                  {checkoutInputNote}
                </span>
              </label>
            </div>

            <div className="billing-quote-box">
              {frontendQuote ? (
                <>
                  <p className="billing-quote-loading" style={{ marginBottom: 10 }}>
                    Estimated preview. Final payable amount is validated by the secure billing server during order creation.
                  </p>
                  <div className="billing-quote-row">
                    <span>Plan</span>
                    <strong>{checkoutTier.name}</strong>
                  </div>
                  <div className="billing-quote-row">
                    <span>{checkoutIsFreeTier ? "Systems" : checkoutIsRenewal ? "Renewal Seats" : "Agents"}</span>
                    <strong>{frontendQuote.agentCount}</strong>
                  </div>
                  <div className="billing-quote-row">
                    <span>{checkoutUnitLabel}</span>
                    <strong>{formatPrice(frontendQuote.unitAmountInPaise, frontendQuote.currency)}</strong>
                  </div>
                  <div className="billing-quote-row">
                    <span>Subtotal</span>
                    <strong>{formatPrice(frontendQuote.subtotalAmountInPaise, frontendQuote.currency)}</strong>
                  </div>
                  <div className="billing-quote-row">
                    <span>GST (18%)</span>
                    <strong>{formatPrice(frontendQuote.taxAmountInPaise, frontendQuote.currency)}</strong>
                  </div>
                  <div className="billing-quote-row billing-quote-row--total">
                    <span>{checkoutTotalLabel}</span>
                    <strong>{formatPrice(frontendQuote.totalAmountInPaise, frontendQuote.currency)}</strong>
                  </div>
                </>
              ) : (
                <p className="billing-quote-loading">{checkoutEmptyQuoteText}</p>
              )}
            </div>

            {checkoutTier.requiresPayment && !checkoutTier.contactSales ? (
              <div className="billing-policy-box">
                <label className="billing-policy-check">
                  <input
                    type="checkbox"
                    checked={policyAcceptance}
                    onChange={(e) => setPolicyAcceptance(e.target.checked)}
                    disabled={checkoutSubmitting || !!processingPlanId}
                  />
                  <span>
                    I agree to the{" "}
                    <span
                      className="billing-tnc-link"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open("/terms", "_blank");
                      }}
                    >
                      Terms &amp; Conditions
                    </span>
                    , Refund Policy, and Payment Policy.
                  </span>
                </label>
              </div>
            ) : null}

            {checkoutError ? <p className="billing-modal-error">{checkoutError}</p> : null}

            <div className="billing-modal-actions">
              <button
                type="button"
                className="billing-modal-cancel"
                onClick={closeCheckoutModal}
                disabled={checkoutSubmitting || !!processingPlanId}
              >
                Cancel
              </button>
              <button
                type="button"
                className="billing-modal-confirm"
                onClick={() => {
                  void handleCheckoutConfirm();
                }}
                disabled={checkoutBlocked}
              >
                {checkoutSubmitting || !!processingPlanId ? "Processing..." : checkoutActionLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
